import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Check, Calendar, CalendarClock, Trophy, Coins, Gift } from 'lucide-react';
import { i18n, tb } from '../utils/i18n';

interface BoomEventPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onParticipate: () => void;
  onDontShowAgain: () => void;
  onNavigateToNews?: () => void;
  onWelcomeBonusClick?: () => void;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export function BoomEventPopup({ isOpen, onClose, onParticipate, onDontShowAgain, onNavigateToNews, onWelcomeBonusClick }: BoomEventPopupProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // 종료일: 2025.11.20 오후 4시
  const endDate = new Date('2025-11-20T16:00:00').getTime();

  useEffect(() => {
    if (!isOpen) return;

    const updateTimer = () => {
      const now = new Date().getTime();
      const distance = endDate - now;

      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);

    return () => clearInterval(timer);
  }, [isOpen, endDate]);

  if (!isOpen) return null;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  };

  const handleDontShowAgain = () => {
    onDontShowAgain();
    handleClose();
  };

  const handleParticipate = () => {
    onParticipate();
    handleClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40 p-4">
      <div
        className={`bg-white dark:bg-gray-800 rounded-3xl p-6 w-full max-w-sm shadow-[0_12px_35px_rgba(0,0,0,0.15)] transform transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
      >
        <div className="flex justify-end mb-2">
          <button
            onClick={handleClose}
            className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        <div className="text-center mb-5">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{tb('boomEventPromoTitle')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-4 whitespace-pre-line">
            {tb('boomEventPromoRemaining')}
          </p>
          
          {/* 100wHANDY 웰컴보너스 받기 버튼 */}
          {onWelcomeBonusClick && (
            <Button
              onClick={() => {
                onWelcomeBonusClick();
                handleClose();
              }}
              className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold rounded-2xl shadow-lg mb-4"
            >
              <Gift className="w-5 h-5 mr-2" />
              {i18n.t('boomWelcomeBonus100')}
            </Button>
          )}
        </div>

        {/* COUNTDOWN 주석 처리 */}
        {/* <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-2xl p-4 mb-5 border border-orange-100 dark:border-orange-900/40">
          <p className="text-xs font-semibold text-orange-600 tracking-[0.3em] text-center mb-3">COUNTDOWN</p>
          <div className="flex justify-center gap-2">
            {[
              { label: 'DAYS', value: timeLeft.days },
              { label: 'HRS', value: timeLeft.hours },
              { label: 'MIN', value: timeLeft.minutes },
              { label: 'SEC', value: timeLeft.seconds }
            ].map((item) => (
              <div
                key={item.label}
                className="bg-white dark:bg-gray-800 rounded-2xl px-3 py-2 text-center min-w-[60px] shadow-sm border border-orange-100/60 dark:border-gray-700"
              >
                <div className="text-2xl font-extrabold text-orange-600 dark:text-orange-400">
                  {String(item.value).padStart(2, '0')}
                </div>
                <div className="text-[10px] tracking-[0.3em] text-gray-500 dark:text-gray-400 uppercase font-semibold">
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div> */}

        <div className="bg-gray-50 dark:bg-gray-800/60 rounded-2xl p-4 space-y-4 border border-gray-100 dark:border-gray-700">
          <div className="grid grid-cols-1 gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-2xl p-3 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center text-orange-600">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{i18n.t('boomStartDateLabel')}</p>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">11월 15일 오후 4시</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-2xl p-3 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center text-orange-600">
                  <CalendarClock className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{i18n.t('boomEndDateLabel')}</p>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">11월 20일 오후 4시</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-white dark:bg-gray-800 rounded-2xl px-3 py-3 shadow-sm flex flex-col items-center gap-1 border border-orange-100/70 dark:border-gray-700">
              <Trophy className="w-5 h-5 text-orange-600" />
              <p className="text-xs text-gray-500 dark:text-gray-400">{i18n.t('totalPrizeLabel')}</p>
              <p className="text-base font-extrabold text-orange-600 dark:text-orange-400">180,000 wHANDY</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-2xl px-3 py-3 shadow-sm flex flex-col items-center gap-1 border border-orange-100/70 dark:border-gray-700">
              <Coins className="w-5 h-5 text-orange-600" />
              <p className="text-xs text-gray-500 dark:text-gray-400">{i18n.t('entryFee')}</p>
              <p className="text-base font-extrabold text-orange-600 dark:text-orange-400">3000 wHANDY</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-2xl px-3 py-3 shadow-sm flex flex-col items-center gap-1 border border-orange-100/70 dark:border-gray-700">
              <Calendar className="w-5 h-5 text-orange-600" />
              <p className="text-xs text-gray-500 dark:text-gray-400">{i18n.t('durationLabel')}</p>
              <p className="text-base font-extrabold text-orange-600 dark:text-orange-400">{i18n.t('durationDays').replace('{days}', '6')}</p>
            </div>
          </div>
        </div>

        <Button
          onClick={handleParticipate}
          className="w-full h-12 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold rounded-2xl shadow-lg"
        >
          {i18n.t('joinBoom2Button')}
        </Button>

        <Button
          onClick={() => {
            if (onNavigateToNews) {
              onNavigateToNews();
            }
            handleClose();
          }}
          className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-semibold rounded-2xl shadow-lg mt-3"
        >
          <Trophy className="w-4 h-4 mr-2" />
          {i18n.t('boomViewWinnersButton')}
        </Button>

        <div className="flex space-x-3 mt-4">
          <Button
            onClick={handleDontShowAgain}
            variant="outline"
            className="flex-1 h-10 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl"
          >
            <Check className="w-4 h-4 mr-2" />
            {i18n.t('dontShowAgain')}
          </Button>
          <Button
            onClick={handleClose}
            variant="outline"
            className="flex-1 h-10 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl"
          >
            {i18n.t('close')}
          </Button>
        </div>
      </div>
    </div>
  );
}


